import { Component, HostBinding, Inject, OnDestroy, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { ToastrService } from 'ngx-toastr';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { SpaceRepository } from 'src/app/state/space.repository';
import { institutionHasSites } from 'src/app/utilities/sites.utils';

import { BehaviorSubject, filter, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavService } from '../services/nav.service';
import { Course, User } from '../models/user';
import { UNRESOLVED_ALERT_STATUSES } from '../models/alert';
import { UserService } from '../services/user.service';
import { QuestionsService } from '../services/questions.service';
import { AnalyticsDashboardV2Variable, FlagsService, FLAGS } from '../services/flags.service';
import { Feature, AclService } from '../services/acl.service';
import { LANDING_URL, pathSegmentsFromUrl, URL_CONSTANTS } from '../common/utils/url';
import { UiService } from '../services/ui.service';
import { pencilLogoText } from '../app.constants';
import { FeatureManagementService } from '../services/feature-management.service';
import { Personas } from '../services/acl.service';
import { NotificationsService } from '../services/notifications.service';
import { NavBadgeKeys, NavBadgeService } from '../services/nav-badge.service';
import { AlertService } from '../services/alert.service';
import { SettingsService } from '../services/settings.service';
import { panelID } from '../settings/settings.constants';
import { TouchScrollLockService } from '../services/touch-scroll-lock.service';
import { ThemeService } from '../services/theme.service';
import { ScreenSizeService } from '../services/screen-size.service';
import { TopBarVariant } from '../ui/top-bar/top-bar.component';
import { SpacesSwitcherService } from '../services/spaces-switcher.service';
import { FeatureFlagsComponent } from './feature-flags/feature-flags.component';

export enum ExpandedSection {
  Analytics = 'analytics',
  Profiles = 'profiles',
  More = 'more',
}

export enum NavbarItems {
  Spaces = 'Spaces',
  Alerts = 'Alerts',
  Schedule = 'Schedule',
  Notifications = 'Notifications',
  People = 'People',
  Profiles = 'Profiles',
  Files = 'Files',
  Messages = 'Messages',
  Courses = 'Courses',
  Sessions = 'Sessions',
  Analytics = 'Analytics',
  Attendance = 'Analytics/Attendance',
  Engagement = 'Analytics/Engagement',
  Sites = 'Analytics/Sites',
  AnalyticsTeachers = 'Analytics/Teachers',
  Recordings = 'Analytics/Recordings',
  MyProfile = 'My profile',
  Teachers = 'Teachers',
  Settings = 'Settings',
  AdminPortal = 'Admin Portal',
  PracticeRoleplay = 'Practice Roleplay',
  Impersonate = 'Impersonate',
  FeatureFlags = 'Feature Flags',
  Home = 'Home',
}

/** Viewports for which a navbar item config applies to. */
export enum NavbarViewport {
  Mobile,
  Desktop,
  Both,
}

export interface RouteNavbarItemConfig {
  item: NavbarItems;
  /** When true, only the bare `/{path}` route matches (e.g. /spaces, not /spaces/:id). */
  matchRootOnly?: boolean;
  viewport: NavbarViewport;
  variant: TopBarVariant;
  /** Compact `app-top-bar` title for this route. Omit for no title. */
  title?: string;
}

/**
 * Root path segment → navbar config (which sidenav item, top-bar variant/title,
 * and which viewports the entry applies to).
 *
 * Consumers:
 * 1. Compact `app-top-bar` (`topBar$` in `AppComponent`): only for non-desktop, and always
 *    looked up with `NavbarViewport.Mobile`. An entry here means the mobile top bar
 *    will be shown for that page only if its `viewport` is `Mobile` or `Both`.
 * 2. Non-desktop sidenav item allowlist (`visibleNavItems` on `NavBarComponent`):
 *    built from entries whose `viewport` is `Mobile` or `Both`. An entry here means the sidenav item
 *    will be shown in the non-desktop sidenav only if its `viewport` is `Mobile` or `Both`.
 *
 * Desktop flows for sidenav or top bar does **not** use this map today, instead all navbar items
 * are considered to be shown and the top bar simply is not rendered on desktop, respectively.
 */
export const ROUTE_TO_NAVBAR_ITEM: ReadonlyMap<string, RouteNavbarItemConfig> = new Map<
  string,
  RouteNavbarItemConfig
>([
  [
    URL_CONSTANTS.HOME,
    {
      item: NavbarItems.Home,
      viewport: NavbarViewport.Mobile,
      variant: 'gray',
    },
  ],
  [
    URL_CONSTANTS.SPACES,
    {
      item: NavbarItems.Spaces,
      matchRootOnly: true,
      viewport: NavbarViewport.Mobile,
      variant: 'solid',
      title: _('Spaces'),
    },
  ],
]);

/**
 * Looks up compact-nav config for a URL, honoring `matchRootOnly` and optional `viewport`.
 * Omit `viewport` to skip viewport filtering. `Both` on the config means valid on any viewport.
 */
export function getRouteNavbarItemConfigFromUrl(
  url: string,
  viewport?: NavbarViewport.Mobile | NavbarViewport.Desktop,
): RouteNavbarItemConfig | undefined {
  const segments = pathSegmentsFromUrl(url);
  const config = ROUTE_TO_NAVBAR_ITEM.get(segments[0]);

  const isMatchRootOnly = !!config?.matchRootOnly;
  const hasChildSegments = segments.length !== 1;
  const shouldFilterByViewport = viewport !== undefined;
  const appliesToViewport =
    config?.viewport === NavbarViewport.Both || config?.viewport === viewport;

  if (
    !config ||
    (isMatchRootOnly && hasChildSegments) ||
    (shouldFilterByViewport && !appliesToViewport)
  ) {
    return undefined;
  }

  return config;
}

@UntilDestroy()
@Component({
  selector: 'app-impersonate-dialog',
  templateUrl: 'impersonate-dialog.html',
  styleUrls: ['./impersonate.component.scss'],
})
export class ImpersonateDialogComponent {
  email = '';

  constructor(
    private auth: Auth,
    public dialogRef: MatDialogRef<ImpersonateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private flagsService: FlagsService,
    private toastrService: ToastrService,
  ) {}

  impersonate(): void {
    if (!this.email) {
      return;
    }
    this.userService
      .impersonate(this.email)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res && res.token) {
          this.flagsService.resetFlags();
          signInWithCustomToken(this.auth, res.token).then(() => {
            location.reload();
          });
        } else {
          this.toastrService.error(res.message);
        }
      });
  }
}

@UntilDestroy()
@Component({
  selector: 'ui-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements AfterViewInit, OnDestroy {
  isDesktop = true;

  /** Drive non-desktop layout styles from the same flag as structural *ngIfs. */
  @HostBinding('class.nav-bar--non-desktop')
  get isNonDesktopLayout(): boolean {
    return !this.isDesktop;
  }

  /** Viewport-aware sidenav allowlist derived from `ROUTE_TO_NAVBAR_ITEM`. */
  visibleNavItems: ReadonlySet<NavbarItems> = this.buildVisibleNavItems();

  active = '';
  courses: Course[] = [];
  userId = '';
  expanded: ExpandedSection | '' = '';
  user?: User;
  protected readonly ExpandedSection = ExpandedSection;
  isImpersonateClicked = false;
  isFeatureFlagsClicked = false;
  isSettingsClicked = false;
  isUsingCustomFlags = false;
  selectedCourseId = '';
  isProfilesPageEnabled = false;
  Features = Feature;
  isComprehensionEnabled = false;
  isGuestUser = false;
  hidePoweredByPencil = false;
  pencilLogoText = pencilLogoText;
  customProductName: SafeHtml | undefined = undefined;
  public isAttendanceReportEnabled = false;
  public isAnalyticstReportEnabled = false;
  public isSitesEnabled = false;
  public showNotificationsSidebarItem = false;
  public notificationsCount = 0;
  protected readonly NavBarItems = NavbarItems;
  protected readonly URL_CONSTANTS = URL_CONSTANTS;
  protected readonly NavBadgeKeys = NavBadgeKeys;
  private hasInitializedNotifications = false;

  readonly isSpacesSwitcherEnabled$ = this.spacesSwitcherService.isEnabled$;
  readonly sidebarLogoText$ = new BehaviorSubject<string>(pencilLogoText);
  readonly notificationsBadge$ = new BehaviorSubject<string | number | undefined>(undefined);
  readonly showControlCenterAlerts$ = new BehaviorSubject<boolean>(false);
  readonly showScheduleItem$ = new BehaviorSubject<boolean>(false);
  readonly showMessagesItem$ = new BehaviorSubject<boolean>(false);
  readonly showCourses$ = new BehaviorSubject<boolean>(false);
  readonly showSessions$ = new BehaviorSubject<boolean>(false);
  readonly showRecordings$ = new BehaviorSubject<boolean>(false);
  readonly showAnalytics$ = new BehaviorSubject<boolean>(false);
  readonly showTeachersCoaching$ = new BehaviorSubject<boolean>(false);
  readonly showPeople$ = new BehaviorSubject<boolean>(false);
  readonly showFiles$ = new BehaviorSubject<boolean>(false);
  readonly showLegacyProfiles$ = new BehaviorSubject<boolean>(false);
  readonly showSettings$ = new BehaviorSubject<boolean>(false);
  readonly showMoreOptions$ = new BehaviorSubject<boolean>(false);
  readonly showAdminPortal$ = new BehaviorSubject<boolean>(false);
  readonly showPracticeRoleplay$ = new BehaviorSubject<boolean>(false);
  readonly showImpersonate$ = new BehaviorSubject<boolean>(false);
  readonly showFeatureFlags$ = new BehaviorSubject<boolean>(false);

  cleanupTouchScrollPrevention: () => void = () => {};

  ngAfterViewInit(): void {
    const sidebar = document.getElementsByClassName('sidebar')[0] as HTMLElement;
    this.cleanupTouchScrollPrevention =
      this.touchScrollLockService.registerTouchScrollPrevention(sidebar);
  }

  ngOnDestroy(): void {
    this.cleanupTouchScrollPrevention();
  }

  private get canAccessAdminMoreFlow(): boolean {
    if (!this.user) {
      return false;
    }

    return (
      this.aclService.isAdminOrSiteAdmin(this.user) ||
      this.aclService.isSuperAdmin(this.user) ||
      this.aclService.isPencilAdmin(this.user) ||
      this.aclService.isAllowed(this.user, Feature.isRootAdmin)
    );
  }

  private userHasFeature(feature: Feature): boolean {
    return !!this.user && this.aclService.isAllowed(this.user, feature);
  }

  private updateUserDerivedState(): void {
    this.sidebarLogoText$.next(this.user?.institution?.name || this.pencilLogoText);
    this.updatePrimaryNavVisibility();
    this.updateToolsVisibility();
    this.updateAdminVisibility();
  }

  private updateNotificationsBadge(): void {
    if (this.notificationsCount <= 0) {
      this.notificationsBadge$.next(undefined);
    } else {
      this.notificationsBadge$.next(this.notificationsCount > 99 ? '99+' : this.notificationsCount);
    }
  }

  private updatePrimaryNavVisibility(): void {
    this.showControlCenterAlerts$.next(
      !!this.user &&
        (this.aclService.isAllowed(this.user, Feature.isAdmin) ||
          this.aclService.isAllowed(this.user, Feature.isSiteAdmin)) &&
        this.featureManagementService.isControlCenterAlertsEnabled(),
    );
    this.showScheduleItem$.next(
      !!this.user &&
        this.aclService.isAllowed(this.user, Feature.view_schedule_page) &&
        this.featureManagementService.isSchedulingEnabled(),
    );
    this.showMessagesItem$.next(
      this.featureManagementService.isGlobalMessagingEnabled() &&
        !!this.user &&
        this.aclService.isAllowed(this.user, Feature.chat),
    );
  }

  private updateToolsVisibility(): void {
    const showCourses = this.featureManagementService.isLmsEnabled() && !this.isGuestUser;
    const isNewSessionsFlowEnabled =
      !!this.user && this.flagsService.isFlagEnabled(FLAGS.ENABLE_NEW_SESSIONS_FLOW);
    const showSessions = isNewSessionsFlowEnabled;
    const showRecordings = !isNewSessionsFlowEnabled;
    const showTeachersCoaching = this.flagsService.isAnalyticsDashboardV2Enabled(
      AnalyticsDashboardV2Variable.TeachersCoaching,
    );
    const showAnalytics =
      (this.isAttendanceReportEnabled || this.isAnalyticstReportEnabled || showTeachersCoaching) &&
      !!this.user &&
      this.aclService.isAdminOrSiteAdmin(this.user) &&
      !this.uiService.isMobile.getValue();
    const showPeople =
      !!this.user &&
      this.aclService.isAllowed(this.user, Feature.ProfilesPage) &&
      this.isProfilesPageEnabled;
    const showFiles =
      this.featureManagementService.isPencilFilesEnabled() &&
      !!this.user &&
      this.aclService.isAllowed(this.user, Feature.view_resource);
    const showLegacyProfiles =
      !!this.user &&
      this.aclService.isAllowed(this.user, Feature.ProfilesPage) &&
      !this.isProfilesPageEnabled;
    const showSettings = !!this.user && !this.aclService.isAnonymous(this.user);

    this.showCourses$.next(showCourses);
    this.showSessions$.next(showSessions);
    this.showRecordings$.next(showRecordings);
    this.showAnalytics$.next(showAnalytics);
    this.showTeachersCoaching$.next(showTeachersCoaching);
    this.showPeople$.next(showPeople);
    this.showFiles$.next(showFiles);
    this.showLegacyProfiles$.next(showLegacyProfiles);
    this.showSettings$.next(showSettings);
  }

  private updateAdminVisibility(): void {
    const canAccess = this.canAccessAdminMoreFlow;
    const showAdminPortal = canAccess && this.userHasFeature(Feature.AdminPage);
    const showImpersonate = canAccess && this.userHasFeature(Feature.Impersonate);
    const showFeatureFlags = canAccess && this.userHasFeature(Feature.CustomFeatures);

    // Practice Roleplay is a top-level sidebar item (under Messages), not part of the
    // Internal Tools section — so it's gated on its own, not on `showMoreOptions`.
    const showPracticeRoleplay = canAccess;

    this.showAdminPortal$.next(showAdminPortal);
    this.showPracticeRoleplay$.next(showPracticeRoleplay);
    this.showImpersonate$.next(showImpersonate);
    this.showFeatureFlags$.next(showFeatureFlags);
    this.showMoreOptions$.next(
      canAccess && (showAdminPortal || showImpersonate || showFeatureFlags),
    );
  }

  constructor(
    public navService: NavService,
    public dialog: MatDialog,
    public router: Router,
    private userService: UserService,
    private route: ActivatedRoute,
    private questionsService: QuestionsService,
    private flagsService: FlagsService,
    public aclService: AclService,
    public uiService: UiService,
    public spaceRepo: SpaceRepository,
    public featureManagementService: FeatureManagementService,
    private sanitizer: DomSanitizer,
    private notificationsService: NotificationsService,
    public navBadgeService: NavBadgeService,
    private alertService: AlertService,
    private settingsService: SettingsService,

    private touchScrollLockService: TouchScrollLockService,
    public themeService: ThemeService,
    private screenSizeService: ScreenSizeService,
    private spacesSwitcherService: SpacesSwitcherService,
  ) {
    this.showNotificationsSidebarItem = this.flagsService.isFlagEnabled(FLAGS.NOTIFICATIONS_BELL);
    this.isProfilesPageEnabled = this.flagsService.isFlagEnabled(FLAGS.PROFILES_PAGE);
    this.isComprehensionEnabled = this.flagsService.isFlagEnabled(FLAGS.ENABLE_COMPREHENSION);
    this.isUsingCustomFlags = this.flagsService.customFeatureFlags != null;

    this.screenSizeService.isDesktop$.pipe(untilDestroyed(this)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
      this.visibleNavItems = this.buildVisibleNavItems();
    });

    this.initUserService();
    this.initRouterService();

    this.flagsService
      .featureFlagChanged(FLAGS.PROJECT_SAM)
      .pipe(
        untilDestroyed(this),
        filter((flagStatus) => !!flagStatus),
      )
      .subscribe(() => {
        this.isAttendanceReportEnabled =
          !!this.flagsService.featureFlagsVariables.project_sam.attendance_report;
        this.isAnalyticstReportEnabled =
          !!this.flagsService.featureFlagsVariables.project_sam.analytics_report;
        this.updateToolsVisibility();
      });

    this.flagsService
      .featureFlagChanged(FLAGS.ANALYTICS_DASHBOARDS_V2)
      .pipe(
        untilDestroyed(this),
        filter((flagStatus) => !!flagStatus),
      )
      .subscribe(() => {
        this.updateToolsVisibility();
      });

    this.questionsService.selectedCourseId.pipe(untilDestroyed(this)).subscribe((res) => {
      this.selectedCourseId = res;
      this.getActive();
    });

    this.uiService.isMobile.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateToolsVisibility();
    });

    this.initAlertsNavBadge();
  }

  initUserService(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res: any) => {
      if (res?.user as User) {
        this.userId = res.user._id;
        this.courses = res.user.courses;
        this.user = res.user;
        this.isGuestUser = this.aclService.isGuest(this.user!);
        this.isSitesEnabled = institutionHasSites(res?.user.institution);

        this.checkPoweredByPencilSetting();
        this.updateUserDerivedState();
        this.initNotificationsService();
      }
    });
  }

  /**
   * Fetches open/in-progress alerts once on init so the nav badge is populated
   * before the user visits the Alerts page. While on that page, AlertsComponent
   * keeps the count in sync via refresh; the template reads count$ and formats with navBadgeFormat.
   */
  initAlertsNavBadge(): void {
    if (!this.featureManagementService.isControlCenterAlertsEnabled()) {
      return;
    }

    this.alertService
      .getAlerts(UNRESOLVED_ALERT_STATUSES)
      .pipe(
        catchError(() => of([])),
        untilDestroyed(this),
      )
      .subscribe((alerts) => this.navBadgeService.setCount(NavBadgeKeys.Alerts, alerts.length));
  }

  initNotificationsService(): void {
    if (!this.showNotificationsSidebarItem || this.hasInitializedNotifications) {
      return;
    }

    this.hasInitializedNotifications = true;

    this.notificationsService.notificationCount.pipe(untilDestroyed(this)).subscribe((count) => {
      this.notificationsCount = Number(count) || 0;
      this.updateNotificationsBadge();
    });

    this.notificationsService
      .getNotificationsCount()
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        this.notificationsService.notificationCount.next(Number(res?.notification_count) || 0);
      });
  }

  initRouterService(): void {
    this.route.params.pipe(untilDestroyed(this)).subscribe(() => {
      this.getActive();
    });

    this.route.queryParams.pipe(untilDestroyed(this)).subscribe(() => {
      this.getActive();
    });

    this.router.events.pipe(untilDestroyed(this)).subscribe((res) => {
      if (res instanceof NavigationEnd) {
        this.getActive();
      }
    });
  }

  getActive(): void {
    const previousActive = this.active;
    // Active-state route checks should ignore query params and hashes.
    const routePath = new URL(this.router.url, window.location.origin).pathname;

    if (routePath.startsWith(`/${LANDING_URL}`)) {
      this.active = NavbarItems.Home;
    } else if (routePath.startsWith('/content/files')) {
      this.active = NavbarItems.Files;
    } else if (routePath.startsWith('/content')) {
      this.active = NavbarItems.Courses;
    } else if (routePath.startsWith('/messages')) {
      this.active = NavbarItems.Messages;
    } else if (routePath.startsWith('/alerts')) {
      this.active = NavbarItems.Alerts;
    } else if (routePath.startsWith('/schedule')) {
      this.active = NavbarItems.Schedule;
    } else if (routePath.startsWith('/notifications')) {
      this.active = NavbarItems.Notifications;
    } else if (routePath.startsWith('/settings')) {
      this.active = NavbarItems.Settings;
    } else if (routePath.startsWith('/admin')) {
      this.active = NavbarItems.AdminPortal;
    } else if (routePath.startsWith('/roleplay')) {
      this.active = NavbarItems.PracticeRoleplay;
    } else if (routePath.startsWith('/profiles')) {
      this.active = NavbarItems.People;
    } else if (routePath.startsWith('/analytics/sites')) {
      this.active = NavbarItems.Sites;
    } else if (routePath.startsWith('/analytics/teachers')) {
      this.active = NavbarItems.AnalyticsTeachers;
    } else if (routePath.startsWith('/analytics/attendance')) {
      this.active = NavbarItems.Attendance;
    } else if (routePath.startsWith('/analytics/engagement')) {
      this.active = NavbarItems.Engagement;
    } else if (routePath.startsWith('/analytics/recordings')) {
      this.active = NavbarItems.Recordings;
    } else if (routePath.startsWith('/sessions')) {
      this.active = NavbarItems.Sessions;
    } else if (routePath.startsWith('/analytics')) {
      this.active = NavbarItems.Analytics;
    } else if (routePath.startsWith('/spaces')) {
      this.active = NavbarItems.Spaces;
    } else {
      this.active = '';
      this.courses.forEach((c) => {
        if (c._id === this.selectedCourseId) {
          this.active = c.name;
        }
      });
    }

    this.expandAnalyticsForActiveRoute(previousActive);

    if (!this.router.url.includes('course/settings')) {
      this.navService.headerTitle.next(this.active.split('/')[0]);
    }
  }

  private expandAnalyticsForActiveRoute(previousActive: string): void {
    // Entering an analytics child route should reveal its row, but manual collapse remains
    // available once the user navigates away from analytics children.
    if (this.isAnalyticsChildItem(this.active) && !this.isAnalyticsChildItem(previousActive)) {
      this.expanded = ExpandedSection.Analytics;
    }
  }

  private isAnalyticsChildItem(activeItem: string): boolean {
    // Child items are stored as "Analytics/<section>" so the parent can manage accordion state.
    return activeItem.startsWith(`${NavbarItems.Analytics}/`);
  }

  toggleAnalytics(): void {
    this.expanded = this.expanded === ExpandedSection.Analytics ? '' : ExpandedSection.Analytics;
  }

  goTo(name: string): void {
    // Close the overlay on tablet/mobile so it doesn't stay open after navigating.
    this.closeNavbar();
    switch (name) {
      case NavbarItems.Home:
        this.router.navigate([`/${LANDING_URL}`]);
        break;
      case NavbarItems.Courses:
        this.router.navigate(['/content']);
        break;
      case NavbarItems.Messages:
        this.router.navigate(['messages', 'home'], { queryParamsHandling: 'merge' });
        break;
      case NavbarItems.Alerts:
        this.router.navigate(['/alerts']);
        break;
      case NavbarItems.Schedule:
        this.router.navigate(['/schedule']);
        break;
      case NavbarItems.Notifications:
        this.router.navigate(['/notifications']);
        break;
      case NavbarItems.AdminPortal:
        this.router.navigate(['/admin']);
        break;
      case NavbarItems.PracticeRoleplay:
        this.router.navigate(['/roleplay']);
        break;
      case NavbarItems.Profiles:
        this.router.navigate(['/profiles']);
        break;
      case NavbarItems.Spaces:
        this.router.navigate([`/${URL_CONSTANTS.SPACES}`]);
        break;
      case NavbarItems.Attendance:
        this.router.navigate(['/analytics/attendance']);
        break;
      case NavbarItems.Engagement:
        this.router.navigate(['/analytics/engagement']);
        break;
      case NavbarItems.AnalyticsTeachers:
        this.router.navigate(['/analytics/teachers']);
        break;
      case NavbarItems.Recordings:
        this.router.navigate(['/analytics/recordings']);
        break;
      case NavbarItems.Sites:
        // Navigate to site analytics with default student filter
        this.router.navigate(['/analytics/sites'], {
          queryParams: { roles: Personas.Student },
        });
        break;
      case NavbarItems.Files:
        this.router.navigate(['/content/files']);
        break;
      case NavbarItems.Sessions:
        this.router.navigate(['/sessions']);
        break;
    }
  }

  showSettings(): void {
    if (!this.showSettings$.getValue() || this.isSettingsClicked) {
      return;
    }

    this.isSettingsClicked = true;
    this.active = NavbarItems.Settings;
    this.settingsService.showSettingsPanel(panelID.account);

    const settingsClosed$ =
      this.settingsService.dialogRef?.afterClosed() ??
      this.settingsService.bottomSheetRef?.afterDismissed();

    settingsClosed$?.pipe(untilDestroyed(this)).subscribe(() => {
      this.isSettingsClicked = false;
      this.getActive();
    });
  }

  impersonate(): void {
    this.isImpersonateClicked = true;
    const dialogRef = this.dialog.open(ImpersonateDialogComponent, {
      width: '340px',
      panelClass: 'impersonate-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isImpersonateClicked = false;
      });
  }

  showFeatureFlags(): void {
    this.isFeatureFlagsClicked = true;
    const dialogRef = this.dialog.open(FeatureFlagsComponent, {
      width: '600px',
      panelClass: 'feature-flags-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isFeatureFlagsClicked = false;
      });
  }

  checkPoweredByPencilSetting(): void {
    // Default is show, which will be the case for
    // institution which don't have settings or hidePoweredByPencil is false
    // 'hidePoweredByPencil' corresponds to 'Hide product name text on sidebar' in the admin panel
    if (this.user?.institution?.settings) {
      this.hidePoweredByPencil = this.user.institution.settings.hidePoweredByPencil;

      const institutionProductName = this.user.institution.settings.productName?.trim();
      this.customProductName = institutionProductName
        ? this.sanitizer.bypassSecurityTrustHtml(institutionProductName)
        : undefined;
    }
  }

  closeNavbar(): void {
    if (!this.isDesktop) {
      this.navService.isMenuOpened.next(false);
    }
  }

  private buildVisibleNavItems(): ReadonlySet<NavbarItems> {
    if (this.isDesktop) {
      return new Set(Object.values(NavbarItems));
    }

    return new Set(
      [...ROUTE_TO_NAVBAR_ITEM.values()]
        .filter(
          (config) =>
            config.viewport === NavbarViewport.Mobile || config.viewport === NavbarViewport.Both,
        )
        .map((config) => config.item),
    );
  }
}
